/**
 * Downgrade Reconsider Modal
 *
 * Single modal shown when a user attempts to downgrade from a paid trial.
 * Variant (Evidence Pro / Clinician / Practice) is determined by the atom state,
 * which is set by plan selection flows based on currentPlan logic.
 *
 * Figma: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=7503-32547
 */

import { trialEndPlanSelectionModalOpenAtom } from '@/atoms/trialAtoms';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { Button } from '@heidi/design-system/button';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyH2, TypographyP2 } from '@heidi/design-system/typography';
import { ModalId, UserActivityAction } from '@heidi/schema/ml-scribe';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { DowngradeCustomTemplatesSvg } from './downgrade-custom-templates-svg';
import {
  downgradeReconsiderModalStateAtom,
  DowngradeReconsiderModalVariant,
} from './downgrade-reconsider-modal-atoms';
import { downgradeReconsiderModalMessages } from './downgrade-reconsider-modal.messages';
import { DowngradeSourceControlSvg } from './downgrade-source-control-svg';
import { DowngradeTeamAccessSvg } from './downgrade-team-access-svg';

const VARIANT_CONFIG = {
  [DowngradeReconsiderModalVariant.Evidence]: {
    title: downgradeReconsiderModalMessages.evidenceTitle,
    subtitle: downgradeReconsiderModalMessages.evidenceSubtitle,
  },
  [DowngradeReconsiderModalVariant.Clinician]: {
    title: downgradeReconsiderModalMessages.clinicianTitle,
    subtitle: downgradeReconsiderModalMessages.clinicianSubtitle,
  },
  [DowngradeReconsiderModalVariant.Practice]: {
    title: downgradeReconsiderModalMessages.practiceTitle,
    subtitle: downgradeReconsiderModalMessages.practiceSubtitle,
  },
} as const;

// Inline SVG components (translatable labels) converted from the former
// static PNG exports.
const ILLUSTRATION_COMPONENTS = {
  [DowngradeReconsiderModalVariant.Evidence]: DowngradeSourceControlSvg,
  [DowngradeReconsiderModalVariant.Clinician]: DowngradeCustomTemplatesSvg,
  [DowngradeReconsiderModalVariant.Practice]: DowngradeTeamAccessSvg,
} as const;

export const DowngradeReconsiderModal = () => {
  const [state, setState] = useAtom(downgradeReconsiderModalStateAtom);
  const setTrialEndPlanSelectionModalOpen = useSetAtom(
    trialEndPlanSelectionModalOpenAtom
  );
  const { handleClose: handleQueueClose } = useQueuedModal(
    ModalId.TRIAL_END_PLAN_SELECTION
  );
  const { data: userActivity } = useQueryUser((user) => user?.user_activity);
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();

  const open = state.open && state.variant !== null;
  const variant = state.variant ?? DowngradeReconsiderModalVariant.Clinician;
  const config = VARIANT_CONFIG[variant];
  const IllustrationSvg = ILLUSTRATION_COMPONENTS[variant];

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setState({ open: false, variant: null });
      }
    },
    [setState]
  );

  const handleDowngrade = useCallback(() => {
    setState({ open: false, variant: null });
    setTrialEndPlanSelectionModalOpen(false);
    handleQueueClose();
  }, [setState, setTrialEndPlanSelectionModalOpen, handleQueueClose]);

  const handleKeepPro = useCallback(() => {
    setState({ open: false, variant: null });
    setTrialEndPlanSelectionModalOpen(false);
  }, [setState, setTrialEndPlanSelectionModalOpen]);

  const subtitleCount = useMemo(() => {
    const evidenceSourceControlCount =
      userActivity?.[UserActivityAction.evidence_source_control] ?? 0;
    const teamMemberCount = teamDetails?.team.team_members?.length ?? 0;

    if (variant === DowngradeReconsiderModalVariant.Evidence) {
      return evidenceSourceControlCount;
    }
    if (variant === DowngradeReconsiderModalVariant.Practice) {
      return teamMemberCount;
    }
    return 0;
  }, [variant, userActivity, teamDetails?.team?.team_members?.length]);

  if (!open) {
    return null;
  }

  return (
    <DialogDrawer open={open} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        className="flex max-h-[90dvh] w-full flex-col gap-6 overflow-y-auto bg-surface p-8 sm:max-w-md sm:rounded-2xl"
        disableDrag
      >
        <div className="flex flex-col items-center gap-6">
          <div className="relative h-[173px] w-[300px] overflow-hidden rounded-xl">
            {/* xMidYMid slice reproduces the old <Image fill className="object-cover">
                crop for the 300x173 container. */}
            <IllustrationSvg className="size-full" preserveAspectRatio="xMidYMid slice" />
          </div>

          <div className="flex flex-col items-center gap-2 text-center">
            <TypographyH2 className="text-text-primary">
              <FormattedMessage {...config.title} />
            </TypographyH2>
            <TypographyP2 className="text-text-secondary">
              <FormattedMessage {...config.subtitle} values={{ count: subtitleCount }} />
            </TypographyP2>
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-6">
          <Button
            variant="destructive_soft"
            size="md"
            className="w-full"
            onClick={handleDowngrade}
            aria-busy={false}
          >
            <FormattedMessage {...downgradeReconsiderModalMessages.downgradeButton} />
          </Button>

          <Button variant="ghost" size="md" className="w-full" onClick={handleKeepPro}>
            <FormattedMessage {...downgradeReconsiderModalMessages.goBackButton} />
          </Button>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};
